import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { GOVERNANCE_COPY, formatProvenanceTimestamp } from '@/lib/governanceCopy';

export interface DataProvenanceBadgeProps {
  /** Optional timestamp of the last data refresh shown to the user. */
  updatedAt?: Date | string | null;
  /** Visual density. */
  variant?: 'compact' | 'default';
  className?: string;
}

/**
 * DataProvenanceBadge
 *
 * Discreet "Fonte certificada" chip designed to sit next to KPI cards or
 * indicator headers. Purely presentational — receives values via props,
 * performs no network calls and never exposes technical names (no view,
 * table, RPC, hash or any internal identifier).
 *
 * Copy is centralized in `src/lib/governanceCopy.ts` to allow project-wide
 * rollback by reverting a single file.
 */
export const DataProvenanceBadge: React.FC<DataProvenanceBadgeProps> = ({
  updatedAt,
  variant = 'default',
  className,
}) => {
  const ts = formatProvenanceTimestamp(updatedAt);
  const isCompact = variant === 'compact';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full',
              'border border-border/60 bg-muted/40 text-muted-foreground',
              'transition-colors hover:text-foreground hover:border-foreground/30',
              isCompact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]',
              'font-medium',
              className,
            )}
            aria-label={GOVERNANCE_COPY.provenance.tooltip}
          >
            <ShieldCheck
              className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3')}
              strokeWidth={1.8}
            />
            <span>{GOVERNANCE_COPY.provenance.label}</span>
            {ts && (
              <>
                <span aria-hidden className="opacity-50">·</span>
                <span className="opacity-80">{ts}</span>
              </>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] text-xs">
          {GOVERNANCE_COPY.provenance.tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default DataProvenanceBadge;
